import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { DigitsOnlyDirective } from '../core/directives';
import { environment } from '../environments/environment';
import { Reservation } from '../core/models/equipment.models';
import { EquipmentService } from '../core/services/equipment.service';
import { ExportService } from '../core/services/export.service';
import { InventoryService } from '../core/services/inventory.service';
import { SettingsService } from '../core/services/settings.service';
import { confirmAction } from '../core/utils/confirm.util';
import {
  defaultBranchFilter,
  defaultBranchFilterId,
  isBranchFilterLocked,
  matchesBranchFilter,
} from '../core/utils/branch-scope.util';
import {
  EQUIPMENT_RENTAL_FLOW,
  reservationStatusLabel,
} from '../core/utils/equipment-rental.util';
import { AuthService } from '../modules/auth/auth.service';
import { currentTimestamp, formatDate } from '../core/utils/format.util';
import { assertCanTake, parsePositiveInt } from '../core/utils/stock-qty.util';

@Component({
  selector: 'app-reservations',
  standalone: true,
  imports: [FormsModule, DigitsOnlyDirective],
  templateUrl: './reservations.component.html',
  styleUrls: ['./reservations.component.css'],
})
export class ReservationsComponent implements OnInit {
  private readonly equipmentService = inject(EquipmentService);
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  private readonly auth = inject(AuthService);

  readonly timestamp = currentTimestamp;
  readonly formatDate = formatDate;
  readonly reservationStatusLabel = reservationStatusLabel;
  readonly rentalFlowHint = EQUIPMENT_RENTAL_FLOW;
  readonly allReservations = this.equipmentService.allReservations;
  readonly allAssets = this.equipmentService.allAssets;
  readonly branches = this.settings.scopedBranches;
  readonly locations = this.settings.scopedLocations;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));

  // ============================================
  // STATE
  // ============================================
  readonly searchQuery = signal('');
  readonly statusFilter = signal('');
  readonly branchFilter = signal('all');
  readonly locationFilter = signal('all');

  readonly showReservationModal = signal(false);
  readonly editingId = signal<string | null>(null);
  /** Original row — used so a status change does not rewrite equipment/location from a bad refresh. */
  readonly editingSnapshot = signal<Reservation | null>(null);
  readonly isEditing = computed(() => this.editingId() !== null);

  readonly formCustomer = signal('');
  readonly formEquipment = signal('');
  readonly formBranch = signal('');
  readonly formLocation = signal('');
  readonly formStartDate = signal('');
  readonly formEndDate = signal('');
  readonly formQuantity = signal(1);
  readonly formStatus = signal<Reservation['status']>('pending');

  readonly showToast = signal(false);
  readonly toastMessage = signal('');

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilter(this.auth, branches));
    });
  }

  // ============================================
  // COMPUTED
  // ============================================
  readonly equipmentLoading = signal(false);

  readonly availableEquipment = computed(() => {
    const branchId =
      this.formBranch() ||
      (this.branchFilterLocked()
        ? defaultBranchFilterId(this.auth, this.branches())
        : '');
    const branch = this.branches().find((row) => row.id === branchId);
    const locationId = this.formLocation();
    return this.equipmentService.getItemMasterEquipmentForReservation(
      branchId || undefined,
      locationId || undefined,
      branch?.name,
    );
  });

  readonly formBranchLocations = computed(() => {
    const branchId = this.formBranch();
    if (!branchId) {
      return this.locations();
    }
    return this.locations().filter(
      (location) => location.branchId === branchId,
    );
  });

  readonly selectedEquipmentAsset = computed(() => {
    const equipmentId = this.formEquipment();
    if (!equipmentId) {
      return undefined;
    }
    return (
      this.availableEquipment().find(
        (row) => String(row.id) === String(equipmentId),
      ) ??
      this.allAssets().find((row) => String(row.id) === String(equipmentId))
    );
  });

  /** Max units the user can reserve for the current equipment / branch / location. */
  readonly maxReservationQuantity = computed(() => {
    const asset = this.selectedEquipmentAsset();
    if (!asset) {
      return 0;
    }

    const inventoryItemId =
      asset.inventoryItemID ??
      (String(asset.id).startsWith('inv-')
        ? Number(String(asset.id).slice(4)) || 0
        : 0);

    if (
      !inventoryItemId ||
      !this.equipmentService.isQuantityBasedEquipment(asset)
    ) {
      return 1;
    }

    const branchId = this.formBranch();
    const locationId = this.formLocation();
    const location = this.locations().find((row) => row.id === locationId);

    if (locationId) {
      return this.inventory.availableQuantityAtLocation(
        inventoryItemId,
        branchId || undefined,
        locationId,
        location?.name,
      );
    }

    return this.inventory.availableQuantityAtBranch(
      inventoryItemId,
      branchId || undefined,
    );
  });

  readonly quantityHint = computed(() => {
    const asset = this.selectedEquipmentAsset();
    if (!asset) {
      return 'Select equipment to see available quantity.';
    }
    if (!this.equipmentService.isQuantityBasedEquipment(asset)) {
      return 'This tagged unit can only be reserved one at a time.';
    }
    const max = this.maxReservationQuantity();
    const locationId = this.formLocation();
    if (max <= 0) {
      return locationId
        ? 'Available Qty: 0 — no stock at this store location.'
        : 'Available Qty: 0 — no stock at this branch. Receive stock in Inventory first.';
    }
    return locationId
      ? `Available Qty: ${max} at this store location. You cannot reserve more than ${max}.`
      : `Available Qty: ${max} at this branch. Select a location for exact qty.`;
  });

  readonly reservationStats = computed(() => {
    const list = this.allReservations();
    return {
      total: list.length,
      readyToIssue: list.filter((row) => row.status === 'completed').length,
      issuedOut: list.filter((row) => row.status === 'issued').length,
      confirmed: list.filter((row) => row.status === 'confirmed').length,
      pending: list.filter((row) => row.status === 'pending').length,
      cancelled: list.filter((row) => row.status === 'cancelled').length,
    };
  });

  readonly filteredReservations = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();
    const branch = this.branchFilter();
    const location = this.locationFilter();

    return this.allReservations().filter((reservation) => {
      if (status && reservation.status !== status) {
        return false;
      }
      if (
        !matchesBranchFilter(
          branch,
          reservation.branch,
          reservation.branchId,
          this.branches(),
        )
      ) {
        return false;
      }
      if (location !== 'all' && reservation.location !== location) {
        return false;
      }
      if (
        search &&
        !reservation.reservationNumber.toLowerCase().includes(search) &&
        !reservation.customer.toLowerCase().includes(search) &&
        !reservation.equipment.toLowerCase().includes(search) &&
        !(reservation.branch ?? '').toLowerCase().includes(search) &&
        !(reservation.location ?? '').toLowerCase().includes(search)
      ) {
        return false;
      }
      return true;
    });
  });

  // ============================================
  // METHODS
  // ============================================
  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.loadItemMasterAndStock().subscribe(() => {
        this.equipmentService.refreshEquipmentAssetsFromApi().subscribe();
        this.equipmentService.refreshCheckoutQueueFromApi().subscribe();
      });
    }
  }

  findAssetIcon(equipmentName: string): string {
    const asset = this.allAssets().find((row) => row.name === equipmentName);
    return asset?.icon ?? 'bi-box-seam';
  }

  openReservationModal(): void {
    const today = new Date().toISOString().slice(0, 10);
    const defaultBranch = defaultBranchFilterId(this.auth, this.branches());
    const branchId =
      defaultBranch !== 'all' ? defaultBranch : (this.branches()[0]?.id ?? '');

    this.editingId.set(null);
    this.formCustomer.set('');
    this.formEquipment.set('');
    this.formBranch.set(branchId);
    this.formLocation.set('');
    this.formStartDate.set(today);
    this.formEndDate.set(today);
    this.formQuantity.set(1);
    this.formStatus.set('pending');
    this.showReservationModal.set(true);
    document.body.style.overflow = 'hidden';

    if (!environment.useMockData) {
      this.equipmentLoading.set(true);
      this.settings.refreshFromApi();
      this.inventory.loadItemMasterAndStock().subscribe(() => {
        this.equipmentService.refreshEquipmentAssetsFromApi().subscribe(() => {
          this.equipmentLoading.set(false);
        });
      });
      return;
    }
  }

  equipmentOptionId(asset: { id: string | number }): string {
    return String(asset.id);
  }

  onFormEquipmentChange(equipmentId: string): void {
    this.formEquipment.set(String(equipmentId ?? ''));
    this.clampFormQuantity();
  }

  onFormQuantityChange(value: number | string): void {
    const parsed = parsePositiveInt(value, 'Quantity');
    if (!parsed.ok) {
      this.showToastMessage(
        parsed.message ?? 'Enter a valid quantity',
        'error',
      );
      return;
    }
    const max = this.maxReservationQuantity();
    if (max > 0 && parsed.value > max) {
      this.formQuantity.set(max);
      this.showToastMessage(
        `Only ${max} available. Quantity set to ${max}.`,
        'info',
      );
      return;
    }
    this.formQuantity.set(parsed.value);
  }

  private clampFormQuantity(): void {
    const current = this.formQuantity();
    if (!Number.isFinite(current) || current < 1) {
      this.formQuantity.set(1);
      return;
    }
    const max = this.maxReservationQuantity();
    if (max > 0 && current > max) {
      this.formQuantity.set(max);
    }
  }

  onFormBranchChange(branchId: string): void {
    this.formBranch.set(branchId);
    const locations = this.locations().filter(
      (location) => location.branchId === branchId,
    );
    const currentLocation = this.formLocation();
    if (!locations.some((location) => location.id === currentLocation)) {
      this.formLocation.set('');
    }
    const equipment = this.availableEquipment();
    const currentEquipment = String(this.formEquipment() ?? '');
    if (
      currentEquipment &&
      !equipment.some((asset) => String(asset.id) === currentEquipment)
    ) {
      this.formEquipment.set('');
    }
    this.clampFormQuantity();
  }

  onFormLocationChange(locationId: string): void {
    this.formLocation.set(locationId);
    const equipment = this.availableEquipment();
    const currentEquipment = String(this.formEquipment() ?? '');
    if (
      currentEquipment &&
      !equipment.some((asset) => String(asset.id) === currentEquipment)
    ) {
      this.formEquipment.set('');
    }
    this.clampFormQuantity();
  }

  equipmentOptionLabel(asset: {
    id: string | number;
    assetId: string;
    name: string;
    inventoryItemID?: number;
  }): string {
    const base = `${asset.assetId} — ${asset.name}`;
    const locationId = this.formLocation();
    const inventoryItemId =
      asset.inventoryItemID ??
      (String(asset.id).startsWith('inv-')
        ? Number(String(asset.id).slice(4))
        : 0);
    if (
      !locationId ||
      !inventoryItemId ||
      !this.equipmentService.isQuantityBasedEquipment(asset)
    ) {
      return base;
    }
    const branchId = this.formBranch();
    const location = this.locations().find((row) => row.id === locationId);
    const qty = this.inventory.availableQuantityAtLocation(
      inventoryItemId,
      branchId || undefined,
      locationId,
      location?.name,
    );
    return `${base} (${qty} available)`;
  }

  openEditModal(reservation: Reservation): void {
    this.editingSnapshot.set(reservation);
    this.editingId.set(reservation.id);
    this.formCustomer.set(reservation.customer);
    this.formEquipment.set('');
    this.formBranch.set(reservation.branchId ?? '');
    this.formLocation.set(reservation.locationId ?? '');
    this.formStartDate.set(reservation.startDate);
    this.formEndDate.set(reservation.endDate);
    this.formQuantity.set(Math.max(1, reservation.quantityRequested ?? 1));
    this.formStatus.set(reservation.status);
    this.showReservationModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeReservationModal(): void {
    this.showReservationModal.set(false);
    this.editingId.set(null);
    this.editingSnapshot.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeReservationModal();
    }
  }

  saveReservation(): void {
    const editingId = this.editingId();
    const snapshot = this.editingSnapshot();

    if (editingId && snapshot) {
      const status = this.formStatus();
      const startDate = this.formStartDate();
      const endDate = this.formEndDate();
      if (!startDate || !endDate) {
        this.showToastMessage('Start and end dates are required', 'error');
        return;
      }
      this.equipmentService
        .updateReservationViaApi(editingId, {
          status,
          startDate,
          endDate,
          preserveDisplay: {
            customer: snapshot.customer,
            equipment: snapshot.equipment,
            branch: snapshot.branch,
            branchId: snapshot.branchId,
            location: snapshot.location,
            locationId: snapshot.locationId,
            quantityRequested: snapshot.quantityRequested,
            inventoryItemID: snapshot.inventoryItemID,
            equipmentAssetID: snapshot.equipmentAssetID,
          },
        })
        .subscribe((result) => {
          if (result.success) {
            this.closeReservationModal();
            if (status === 'completed') {
              this.showToastMessage(
                'Reservation completed. Equipment is paid and waiting on Check-Out.',
                'success',
              );
            } else if (status === 'cancelled') {
              this.showToastMessage(
                'Completed reservation cancelled. Waiting checkout removed; reservation is Pending again.',
                'success',
              );
            } else {
              this.showToastMessage(
                'Reservation updated successfully',
                'success',
              );
            }
          } else {
            this.showToastMessage(
              result.message ?? 'Failed to update reservation',
              'error',
            );
          }
        });
      return;
    }

    const customer = this.formCustomer().trim();
    const equipmentId = this.formEquipment();
    const branchId = this.formBranch();
    const locationId = this.formLocation();
    const startDate = this.formStartDate();
    const endDate = this.formEndDate();

    if (
      !customer ||
      !equipmentId ||
      !startDate ||
      !endDate ||
      !branchId ||
      !locationId
    ) {
      this.showToastMessage('Please fill in all required fields', 'error');
      return;
    }

    const persist = () => {
      const parsed = parsePositiveInt(this.formQuantity(), 'Quantity');
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Quantity must be at least 1',
          'error',
        );
        return;
      }
      const quantityRequested = parsed.value;
      const maxQuantity = this.maxReservationQuantity();
      const asset =
        this.availableEquipment().find(
          (row) => String(row.id) === String(equipmentId),
        ) ??
        this.allAssets().find((row) => String(row.id) === String(equipmentId));
      const label = asset?.name ?? 'this item';
      if (maxQuantity <= 0) {
        this.showToastMessage(
          'This item has no stock at the selected branch. Receive stock in Inventory first.',
          'error',
        );
        return;
      }
      const check = assertCanTake(maxQuantity, quantityRequested, label);
      if (!check.ok) {
        this.showToastMessage(
          check.message ?? `Only ${maxQuantity} available for ${label}.`,
          'error',
        );
        return;
      }

      const branch = this.branches().find((row) => row.id === branchId);
      const location = this.locations().find((row) => row.id === locationId);
      const inventoryItemID =
        asset?.inventoryItemID ??
        (equipmentId.startsWith('inv-') ? Number(equipmentId.slice(4)) : 0);
      const equipmentAssetID =
        asset && this.equipmentService.isQuantityBasedEquipment(asset)
          ? 0
          : Number(asset?.id) || 0;

      const payload = {
        customer,
        equipment: asset?.name ?? equipmentId,
        branch: branch?.name ?? branchId,
        branchId,
        location: location?.name ?? locationId,
        locationId,
        storeLocationID: Number(locationId) || 0,
        startDate,
        endDate,
        status: this.formStatus(),
        quantityRequested,
        inventoryItemID,
        equipmentAssetID,
        branchID: Number(branchId) || 0,
      };

      this.equipmentService
        .createReservationViaApi(payload)
        .subscribe((result) => {
          if (result.success) {
            this.closeReservationModal();
            this.showToastMessage(
              payload.status === 'completed'
                ? 'Reservation completed. Equipment is paid and waiting on Check-Out.'
                : 'Reservation created successfully',
              'success',
            );
          } else {
            this.showToastMessage(
              result.message ?? 'Failed to create reservation',
              'error',
            );
          }
        });
    };

    this.inventory.loadItemMasterAndStock().subscribe(() => persist());
  }

  deleteReservation(reservation: Reservation): void {
    if (
      !confirmAction(
        reservation.status === 'completed'
          ? `Cancel completed reservation "${reservation.reservationNumber}"? This removes it from Check-Out (paid waiting) and sets it back to Pending.`
          : `Cancel reservation "${reservation.reservationNumber}"?`,
      )
    ) {
      return;
    }

    this.equipmentService
      .deleteReservationViaApi(reservation.id)
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            reservation.status === 'completed'
              ? 'Waiting checkout removed. Stock is available on Current Stock again.'
              : 'Reservation cancelled. Stock returned to Current Stock.',
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to cancel reservation',
            'error',
          );
        }
      });
  }

  readonly toastType = signal<'success' | 'error' | 'info'>('success');

  showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  showSuccessToast(message: string): void {
    this.showToastMessage(message, 'success');
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  exportData(): void {
    const rows = this.filteredReservations();
    if (!rows.length) {
      this.showToastMessage(
        'No reservations match the current filters.',
        'info',
      );
      return;
    }

    this.exporter.downloadCsv(
      `reservations-${new Date().toISOString().slice(0, 10)}`,
      rows.map((item) => ({
        'Reservation ID': item.reservationNumber,
        Customer: item.customer,
        Equipment: item.equipment,
        Quantity: item.quantityRequested ?? 1,
        Branch: item.branch,
        Location: item.location,
        'Start Date': item.startDate,
        'End Date': item.endDate,
        Status: item.status,
      })),
    );
  }
}
